import { configAtom } from "@/config.ts";
import { allIpQueryAtom, type IpAddressWithType } from "@/modules/network/ipAddress/ipAddressApi.ts";
import { UNASSIGNED_RESOURCE } from "@/modules/network/ipAddress/resourceId.ts";
import { pricesAtom } from "@/modules/pricing/pricingQuery.ts";
import { getServicePrice, type KubernetesPrice, type Price } from "@/modules/pricing/resourcePricing.ts";
import { allServicesQueryAtom, type ManagedServiceLoc } from "@/modules/services/servicesQuery.ts";
import { mergeLoadedQueries, type QMergedResult, queryThen } from "@/utils/query/mergeQueries.ts";
import type { ManagedService } from "@warrenio/api-spec/spec.oats.gen";
import { mapFrom, mapOnValues } from "@warrenio/utils/collections/maps";
import { atom } from "jotai/vanilla";

export type ManagedServiceWithAssigned = ManagedServiceLoc & {
    price: Price | KubernetesPrice;
    assignedPublicIp: IpAddressWithType | UNASSIGNED_RESOURCE;
};

export type AssignedServiceResponse = Map<ManagedService["uuid"], ManagedServiceWithAssigned>;

export const allAssignedServicesQueryAtom = atom((get): QMergedResult<AssignedServiceResponse | undefined> => {
    const results = {
        services: get(allServicesQueryAtom),
        ips: get(allIpQueryAtom),
        prices: get(pricesAtom),
    };
    return mergeLoadedQueries(results, ({ services, ips, prices }) => {
        const ipsByAssignedTo = mapFrom(ips.values(), (ip) => ip.assigned_to);

        return mapOnValues(services, (service): ManagedServiceWithAssigned => {
            const { uuid } = service;
            return {
                ...service,
                price: getServicePrice(prices, service),
                assignedPublicIp: ipsByAssignedTo?.get(uuid) ?? UNASSIGNED_RESOURCE,
            };
        });
    });
});

export const availableServicesAtom = atom((get) => {
    return queryThen(get(allAssignedServicesQueryAtom), (service) => {
        const { kubernetesEnabled } = get(configAtom);
        return kubernetesEnabled
            ? service
            : new Map([...service].filter(([_key, value]) => value.service !== "kubernetes"));
    });
});
